import React, { useCallback, useMemo, useState } from 'react';
import { validateRcsConfig } from '../utils/validateRcsConfig';
import { useTranslation } from 'react-i18next';
import { generatePath, useParams } from 'react-router';
import * as RestApi from 'core/network/RestApi';
import useNotifications from 'core/notifications/useNotifications';
import { TemplateSelectorModal } from '@salecycle/experimental/Modal/TemplateSelectorModal';
import {
  Box,
  Button,
  Header,
  Icon,
  Input,
  Select,
  Textarea,
  Toggle,
  Tooltip,
  ISelectOption,
} from '@salecycle/ui-component-library';
import { uuid } from 'utils/uuid';
import { ITemplate } from 'entities/template/template';
import { TemplateApiEndpoints } from 'sections/design/template-management/constants/api-endpoints';
import { TemplateTypes } from 'sections/design/template-management/sections/template-list/constants/templateTypes';
import { useSmsTemplatesWithContent } from '../hooks/useSmsTemplatesWithContent';
import {
  ConfigScrollContainer,
  ConfigSection,
  ConfigSectionHeader,
  ConfigSectionTitle,
  ConfigHeaderWrapper,
  SectionIconWrapper,
  TooltipTriggerButton,
  ConfigContentFields,
  ActionCard,
  ActionCardHeader,
  ActionCardTitle,
  ActionCardSelectWrapper,
  ActionCardFieldsWrapper,
  ChooseTemplateButtonWrapper,
  AddCtaButtonWrapper,
  FallbackSectionHeaderLeading,
  FallbackSectionHeaderRow,
  FallbackSectionRow,
  FallbackTemplateName,
} from './style';
import {
  useRcsTemplateEditorDispatch,
  useRcsTemplateEditorState,
  IRcsActionConfig,
} from '../context/useRcsTemplateEditorContext';
import { MAX_BUTTON_LABEL_LENGTH } from '../utils/validateRcsConfig';

const MAX_ACTION_CARDS = 4;

const SMS_TEMPLATE_TYPES = [TemplateTypes.cartAbandonment, TemplateTypes.browseAbandonment];

const HelpTooltip: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip title={text} position="top">
    <TooltipTriggerButton type="button" aria-label={text}>
      <Icon icon="question-circle" size="small" />
    </TooltipTriggerButton>
  </Tooltip>
);

const SectionHeaderWithTooltip: React.FC<{
  icon: string;
  title: string;
  tooltip: string;
}> = ({ icon, title, tooltip }) => (
  <ConfigSectionHeader>
    <SectionIconWrapper>
      <Icon className="section-icon" icon={icon} />
    </SectionIconWrapper>
    <ConfigSectionTitle>{title}</ConfigSectionTitle>
    <HelpTooltip text={tooltip} />
  </ConfigSectionHeader>
);

export const RcsConfigView: React.FC = () => {
  const { t } = useTranslation('designs');
  const { t: tModal } = useTranslation('templateSelectModal');
  const { showNotification } = useNotifications();
  const { siteId } = useParams<{ siteId: string }>();
  const { rcsConfig, selectedLanguage, editorSettings } = useRcsTemplateEditorState();
  const readOnly = editorSettings.readOnly === true;
  const dispatch = useRcsTemplateEditorDispatch();
  const [smsFallbackModalOpen, setSmsFallbackModalOpen] = useState(false);

  const { getTemplatePreviewContent, templatesQuery } = useSmsTemplatesWithContent(
    selectedLanguage,
    smsFallbackModalOpen,
  );

  const handleSmsFallbackConfirm = useCallback(
    async (template: ITemplate) => {
      if (!template) {
        setSmsFallbackModalOpen(false);
        return;
      }
      if (!siteId) return;
      const resolvedLanguage = template.supportedLanguages?.some(s => s.language === selectedLanguage)
        ? selectedLanguage
        : template.supportedLanguages?.[0]?.language ?? selectedLanguage;
      try {
        const result = await RestApi.get<ITemplate>(
          generatePath(TemplateApiEndpoints.GET_TEMPLATE, {
            siteId,
            templateId: template.id,
            language: resolvedLanguage,
          }),
        );
        if (result?.data) {
          const content = result.data.template ?? '';
          dispatch({
            type: 'SET_RCS_CONFIG',
            payload: {
              smsFallbackEnabled: true,
              smsFallbackTemplateId: template.id,
              smsFallbackTemplateName: template.templateName,
              smsFallbackContent: content,
            },
          });
        }
      } catch (err) {
        showNotification(t('rcsTemplateEditor.configView.smsFallbackError'), 'error');
      } finally {
        setSmsFallbackModalOpen(false);
      }
    },
    [dispatch, selectedLanguage, siteId, showNotification, t],
  );

  const actionTypeOptions: ISelectOption<string>[] = useMemo(
    () => [
      { value: 'openUrl', label: t('rcsTemplateEditor.configView.actionTypeOpenUrl') },
      { value: 'dialPhone', label: t('rcsTemplateEditor.configView.actionTypeDialPhone') },
    ],
    [t],
  );

  const updateRcsConfig = useCallback(
    (payload: Partial<typeof rcsConfig>) => {
      dispatch({ type: 'SET_RCS_CONFIG', payload });
    },
    [dispatch],
  );

  const handleSetSmsFallbackToggleChange = useCallback(
    (enabled: boolean) => {
      updateRcsConfig({
        smsFallbackEnabled: enabled,
        smsFallbackTemplateId: '',
        smsFallbackTemplateName: '',
        smsFallbackContent: '',
      });
    },
    [updateRcsConfig],
  );

  const addAction = useCallback(() => {
    if (rcsConfig.actions.length >= MAX_ACTION_CARDS) return;
    const newAction: IRcsActionConfig = {
      id: uuid(),
      actionType: 'openUrl',
      buttonLabel: '',
      actionUrl: '',
    };
    updateRcsConfig({ actions: [...rcsConfig.actions, newAction] });
  }, [rcsConfig.actions, updateRcsConfig]);

  const removeAction = useCallback(
    (id: string) => {
      if (rcsConfig.actions.length <= 1) return;
      const index = rcsConfig.actions.findIndex(a => a.id === id);
      if (index <= 0) return;
      updateRcsConfig({ actions: rcsConfig.actions.filter(a => a.id !== id) });
    },
    [rcsConfig.actions, updateRcsConfig],
  );

  const updateAction = useCallback(
    (id: string, field: keyof Omit<IRcsActionConfig, 'id'>, value: string | ISelectOption<string>) => {
      const actionTypeValue = typeof value === 'object' && value !== null && 'value' in value ? value.value : value;
      const nextActions = rcsConfig.actions.map(action =>
        action.id === id ? { ...action, [field]: field === 'actionType' ? actionTypeValue : value } : action,
      );
      updateRcsConfig({ actions: nextActions });
    },
    [rcsConfig.actions, updateRcsConfig],
  );

  const actions = rcsConfig.actions;
  const canAddCta = actions.length < MAX_ACTION_CARDS;

  const liveValidation = useMemo(() => validateRcsConfig(rcsConfig), [rcsConfig]);

  const rcsValidationMessage = useCallback(
    (key: string | undefined) => (key ? t(`rcsTemplateEditor.configView.${key}`) : undefined),
    [t],
  );

  return (
    <ConfigScrollContainer data-testid="RcsConfigView">
      <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
        <ConfigHeaderWrapper>
          <Header
            heading={t('rcsTemplateEditor.configView.header')}
            description={t('rcsTemplateEditor.configView.description')}
          />
        </ConfigHeaderWrapper>

        <ConfigSection data-testid="RcsConfigMediaSection">
          <SectionHeaderWithTooltip
            icon="photo-video"
            title={t('rcsTemplateEditor.configView.mediaTitle')}
            tooltip={t('rcsTemplateEditor.configView.mediaTooltip')}
          />
          <Input
            ariaLabel={t('rcsTemplateEditor.configView.imageUrlLabel')}
            label={t('rcsTemplateEditor.configView.imageUrlLabel')}
            value={rcsConfig.imageUrl}
            onChange={value => updateRcsConfig({ imageUrl: value })}
            spacingBelow={true}
            fullWidth
          />
        </ConfigSection>

        <ConfigSection data-testid="RcsConfigContentSection">
          <SectionHeaderWithTooltip
            icon="font"
            title={t('rcsTemplateEditor.configView.contentTitle')}
            tooltip={t('rcsTemplateEditor.configView.contentTooltip')}
          />
          <ConfigContentFields>
            <Box margin="0 0 1rem 0">
              <Input
                ariaLabel={t('rcsTemplateEditor.configView.cardTitleLabel')}
                label={t('rcsTemplateEditor.configView.cardTitleLabel')}
                required
                value={rcsConfig.cardTitle}
                onChange={value => updateRcsConfig({ cardTitle: value })}
                spacingBelow={true}
                fullWidth
                validationState={liveValidation.header ? 'error' : null}
                validationMessage={rcsValidationMessage(liveValidation.header)}
              />
            </Box>
            <Box margin="0">
              <Textarea
                ariaLabel={t('rcsTemplateEditor.configView.descriptionLabel')}
                label={t('rcsTemplateEditor.configView.descriptionLabel')}
                tooltipComponent={<HelpTooltip text={t('rcsTemplateEditor.configView.descriptionResizeHint')} />}
                required
                value={rcsConfig.description}
                onChange={value => updateRcsConfig({ description: value })}
                spacingBelow={true}
                fullWidth
                autoGrow
                height={72}
                verticalResize
                validationState={liveValidation.description ? 'error' : null}
                validationMessage={rcsValidationMessage(liveValidation.description)}
              />
            </Box>
          </ConfigContentFields>
        </ConfigSection>

        <ConfigSection data-testid="RcsConfigActionsSection">
          <SectionHeaderWithTooltip
            icon="link"
            title={t('rcsTemplateEditor.configView.actionsTitle')}
            tooltip={t('rcsTemplateEditor.configView.actionsTooltip')}
          />
          {actions.map((action, index) => {
            const isDialPhone = action.actionType === 'dialPhone';
            const urlOrPhoneLabel = isDialPhone
              ? t('rcsTemplateEditor.configView.dialPhoneNumberLabel')
              : t('rcsTemplateEditor.configView.actionUrlLabel');
            return (
              <ActionCard key={action.id} data-testid={`RcsConfigActionCard-${index}`}>
                <ActionCardHeader>
                  <ActionCardTitle>
                    {t(
                      `rcsTemplateEditor.configView.${
                        ['primaryAction', 'secondaryAction', 'tertiaryAction', 'fourthAction'][index]
                      }`,
                    )}
                  </ActionCardTitle>
                  {index > 0 && (
                    <Button
                      variation="tertiary"
                      ariaLabel={t('rcsTemplateEditor.configView.removeAction')}
                      onClick={() => removeAction(action.id)}
                      leftSideIcon={<Icon icon="times" />}
                    >
                      {t('rcsTemplateEditor.configView.remove')}
                    </Button>
                  )}
                </ActionCardHeader>
                <ActionCardFieldsWrapper>
                  <ActionCardSelectWrapper>
                    <Select<string>
                      ariaLabel={t('rcsTemplateEditor.configView.actionTypeLabel')}
                      label={t('rcsTemplateEditor.configView.actionTypeLabel')}
                      options={actionTypeOptions}
                      value={actionTypeOptions.find(o => o.value === action.actionType) ?? actionTypeOptions[0]}
                      onChange={option => updateAction(action.id, 'actionType', option)}
                      placeholder={t('rcsTemplateEditor.configView.actionTypePlaceholder')}
                      spacingBelow={false}
                      inline
                      searchable={false}
                    />
                  </ActionCardSelectWrapper>
                  <Box margin="0">
                    <Input
                      ariaLabel={t('rcsTemplateEditor.configView.buttonLabelLabel')}
                      label={t('rcsTemplateEditor.configView.buttonLabelLabel')}
                      required
                      value={action.buttonLabel}
                      onChange={value => updateAction(action.id, 'buttonLabel', value)}
                      spacingBelow={false}
                      fullWidth
                      validationState={
                        liveValidation.buttonLabelByActionId?.[action.id] ||
                        (liveValidation.atLeastOneAction && index === 0) ||
                        (action.buttonLabel ?? '').trim().length > MAX_BUTTON_LABEL_LENGTH
                          ? 'error'
                          : null
                      }
                      validationMessage={
                        liveValidation.buttonLabelByActionId?.[action.id]
                          ? rcsValidationMessage(liveValidation.buttonLabelByActionId[action.id])
                          : liveValidation.atLeastOneAction && index === 0
                          ? rcsValidationMessage(liveValidation.atLeastOneAction)
                          : (action.buttonLabel ?? '').trim().length > MAX_BUTTON_LABEL_LENGTH
                          ? rcsValidationMessage('validationButtonLabelMaxLength')
                          : undefined
                      }
                    />
                  </Box>
                  <Box margin="0">
                    <Input
                      ariaLabel={urlOrPhoneLabel}
                      label={urlOrPhoneLabel}
                      tooltipComponent={
                        isDialPhone ? (
                          <HelpTooltip text={t('rcsTemplateEditor.configView.dialPhoneNumberFormatTooltip')} />
                        ) : (
                          undefined
                        )
                      }
                      required
                      value={action.actionUrl}
                      onChange={value => updateAction(action.id, 'actionUrl', value)}
                      spacingBelow={false}
                      fullWidth
                      validationState={
                        liveValidation.actionUrlByActionId?.[action.id] ||
                        (liveValidation.atLeastOneAction && index === 0)
                          ? 'error'
                          : null
                      }
                      validationMessage={
                        liveValidation.actionUrlByActionId?.[action.id]
                          ? rcsValidationMessage(liveValidation.actionUrlByActionId[action.id])
                          : liveValidation.atLeastOneAction && index === 0
                          ? rcsValidationMessage(liveValidation.atLeastOneAction)
                          : undefined
                      }
                    />
                  </Box>
                </ActionCardFieldsWrapper>
                {action.id === actions[actions.length - 1]?.id && canAddCta && (
                  <AddCtaButtonWrapper>
                    <Button
                      variation="secondary"
                      ariaLabel={t('rcsTemplateEditor.configView.addCta')}
                      onClick={addAction}
                      leftSideIcon={<Icon icon="plus" />}
                    >
                      {t('rcsTemplateEditor.configView.addCta')}
                    </Button>
                  </AddCtaButtonWrapper>
                )}
              </ActionCard>
            );
          })}
        </ConfigSection>

        <ConfigSection data-testid="RcsConfigSetFallbackSection">
          <FallbackSectionHeaderRow>
            <FallbackSectionHeaderLeading>
              <SectionIconWrapper>
                <Icon className="section-icon" icon="folder" />
              </SectionIconWrapper>
              <ConfigSectionTitle>{t('rcsTemplateEditor.configView.setFallbackTitle')}</ConfigSectionTitle>
              <Tooltip title={t('rcsTemplateEditor.configView.setFallbackTooltip')} position="top">
                <TooltipTriggerButton type="button" aria-label={t('rcsTemplateEditor.configView.setFallbackTooltip')}>
                  <Icon icon="question-circle" size="small" />
                </TooltipTriggerButton>
              </Tooltip>
            </FallbackSectionHeaderLeading>
            <Toggle
              ariaLabel={t('rcsTemplateEditor.configView.smsFallbackToggleAria')}
              checked={rcsConfig.smsFallbackEnabled}
              onChange={handleSetSmsFallbackToggleChange}
              margin={false}
              testId="RcsSmsFallbackToggle"
            />
          </FallbackSectionHeaderRow>
          {rcsConfig.smsFallbackEnabled && (
            <FallbackSectionRow>
              {rcsConfig.smsFallbackTemplateName ? (
                <>
                  <FallbackTemplateName>{rcsConfig.smsFallbackTemplateName}</FallbackTemplateName>
                  <Button
                    variation="secondary"
                    ariaLabel={t('rcsTemplateEditor.configView.changeTemplate')}
                    onClick={() => setSmsFallbackModalOpen(true)}
                  >
                    {t('rcsTemplateEditor.configView.changeTemplate')}
                  </Button>
                </>
              ) : (
                <ChooseTemplateButtonWrapper>
                  <Button
                    variation="primary"
                    ariaLabel={t('rcsTemplateEditor.configView.chooseTemplate')}
                    onClick={() => setSmsFallbackModalOpen(true)}
                  >
                    {t('rcsTemplateEditor.configView.chooseTemplate')}
                  </Button>
                </ChooseTemplateButtonWrapper>
              )}
            </FallbackSectionRow>
          )}
        </ConfigSection>
      </fieldset>

      <TemplateSelectorModal<ITemplate>
        open={smsFallbackModalOpen}
        onClose={() => setSmsFallbackModalOpen(false)}
        onConfirm={handleSmsFallbackConfirm}
        t={tModal}
        templatesQuery={templatesQuery}
        templateTypes={SMS_TEMPLATE_TYPES}
        getTemplatePreviewContent={getTemplatePreviewContent}
        channel="sms"
        defaultTemplateId={rcsConfig.smsFallbackTemplateId}
        emptyStateMessage={tModal('emptySmsTemplatesForLanguage')}
      />
    </ConfigScrollContainer>
  );
};
